// Navigation actions and account props for the dashboard pages. Both
// dashboard navigation shells, the sidebar and the top bar, render from this
// one object, so a destination cannot differ between them.
//
// Every DashAI destination is reached by route from a dashboard. On /ai the
// page supplies callbacks instead, because each /ai sub-route mounts its own
// DashAI element and a route change would drop the open chat.

import { useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import { DASHAI_ROUTES } from '@dashai/lib/browseNavigation';
import { DASHBOARD_CARDS } from '../data/dashboardCards';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { switchWorkspaceAndUpdateCountry, isDashmoteStaffUser, toSwitcherEntry } from '../slices/authSlice';
import { performLogout } from '../utils/performLogout';
import { canAccessUserManagement } from '../utils/roleUtils';
import {
  togglePinnedWorkspace,
  setSwitcherFocusMode,
  saveSwitcherVisibilityToBackend,
} from '../slices/userPreferencesSlice';

// The product whose countries a workspace switch loads: the module of the
// dashboard on screen. A page that belongs to no module-scoped dashboard loads
// Execution View's, which the server also falls back to for any other product.
export function productForPath(pathname: string): string {
  const card = DASHBOARD_CARDS.find(
    (c) => c.module && (pathname === c.route || pathname.startsWith(`${c.route}/`)),
  );
  return card?.module ?? 'execution_view';
}

export function useAnalyticsNavActions() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { user, userData, selectedWorkspace, availableWorkspaces } = useAppSelector((state) => state.auth);
  const pinnedWorkspaces = useAppSelector((state) => state.userPreferences?.pinned_workspaces || []);
  const focusMode = useAppSelector((state) => state.userPreferences?.switcher_focus_mode || false);
  // Staff / admin come from the single shared predicate in authSlice. It
  // mirrors backend is_dashmote_staff exactly: a
  // @dashmote.com address whose account_type is not "demo". (The /admin gate
  // is the user-level dashmote_admin flag on top of staff.)
  const isStaff = isDashmoteStaffUser(userData);
  const isAdmin = isStaff && userData?.dashmote_admin === true;
  // Same predicate as the /user_management + /settings route guard, so the
  // profile-dropdown nav items only appear when the page is actually reachable.
  const showUserManagement = canAccessUserManagement(user?.email, userData, selectedWorkspace);

  // The switcher list is the server's, exactly as `/users` returned it.
  const allowedClients = useMemo(
    () => (availableWorkspaces || []).map((ws: { key: string }) => ws.key),
    [availableWorkspaces],
  );

  const productName = productForPath(location.pathname);

  const handleClientChange = useCallback((client: string) => {
    dispatch(
      switchWorkspaceAndUpdateCountry({ workspace: toSwitcherEntry(client), product_name: productName }),
    );
  }, [dispatch, productName]);

  // Staff-only switcher visibility: pinned workspaces plus focus mode.
  // Optimistic + best-effort persist (mirrors favorites). UI-only — never
  // changes access.
  const handleTogglePin = useCallback((client: string) => {
    if (!client) return;
    const current: string[] = Array.isArray(pinnedWorkspaces) ? pinnedWorkspaces : [];
    const nextPinned = current.includes(client)
      ? current.filter((k) => k !== client)
      : [...current, client];
    dispatch(togglePinnedWorkspace(client));
    dispatch(saveSwitcherVisibilityToBackend({
      pinned_workspaces: nextPinned,
      switcher_focus_mode: focusMode,
    }));
  }, [dispatch, pinnedWorkspaces, focusMode]);

  const handleToggleFocus = useCallback(() => {
    const next = !focusMode;
    dispatch(setSwitcherFocusMode(next));
    dispatch(saveSwitcherVisibilityToBackend({
      pinned_workspaces: pinnedWorkspaces,
      switcher_focus_mode: next,
    }));
  }, [dispatch, focusMode, pinnedWorkspaces]);

  const handleSignOut = useCallback(() => {
    // Hard-navigate (full index.html reload) so sign-out→re-login lands on the
    // latest bundle instead of the stale one this tab is running.
    performLogout(dispatch);
  }, [dispatch]);

  return {
    onNewChat: () => navigate(DASHAI_ROUTES.newChat),
    onSelectChat: (chatId: string) => navigate(`/ai/chat/${chatId}`),
    onAutonomousAgent: () => navigate(DASHAI_ROUTES.autonomousAgent),
    onProfileMemory: () => navigate('/ai/profile'),
    onSavedCanvas: () => navigate(DASHAI_ROUTES.savedCanvas),
    // The account-menu item is gated on this prop being present, so omitting
    // it leaves Scheduled Reports with no entry point outside DashAI.
    onScheduledReports: () => navigate(DASHAI_ROUTES.scheduledReports),
    onAccountProfile: () => navigate('/ai/account'),
    userName: userData?.name || user?.displayName || '',
    userEmail: user?.email || '',
    userPhotoUrl: user?.photoURL || undefined,
    allowedClients,
    currentClient: selectedWorkspace?.key || null,
    onClientChange: handleClientChange,
    onSignOut: handleSignOut,
    isAdmin,
    isStaff,
    canAccessUserManagement: showUserManagement,
    pinnedWorkspaces,
    focusMode,
    onTogglePinWorkspace: handleTogglePin,
    onToggleFocusMode: handleToggleFocus,
  };
}
